
import {
    AuditModel,
    AuditEscalationGroupModel,
    PermissionTypeEnum,
    QueryOperator,
    TenantUserModel,
    WorkplaceModel,
    GroupModel, EscalationGroupTypeEnum,
} from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import { auditsStore } from '../+state/store';
import { Guid } from '@/libs/common/functions/guid';
import { cloneDeep, isEqual } from 'lodash';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import ShiftTypeSelect from '@/libs/common/components-smart/ShiftTypeSelect.vue';
import { groupsStore } from '@/libs/groups/+state/store';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { auditEscalationGroupsStore } from '@/libs/audit-escalation-groups/+state/store';

@Component({
    components: { ShiftTypeSelect },
})
export default class IssueEscalationGroups extends Page {
    @Prop() tenantId!: string;
    @Prop() auditId!: string;
    districtId: string | null = null;
    audit: AuditModel | null = null;
    auditOriginal: AuditModel | null = null;
    basicRules = [(v: any) => !!v || this.$t('validation.thisFieldIsRequired')];
    deleteDialog = false;
    deleting = false;
    valid = false;
    loading = true;
    saving = false;
    hasChanges = false;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    auditEscalationGroups: { groupId: string; auditEscalationGroup: AuditEscalationGroupModel | null | undefined; group: GroupModel | null | undefined }[] = [];
    selectedGroups: { groupId: string; auditEscalationGroup: AuditEscalationGroupModel | null | undefined; group: GroupModel | null | undefined }[] = [];
    selectedGroupsOriginal: { groupId: string; auditEscalationGroup: AuditEscalationGroupModel | null | undefined; group: GroupModel | null | undefined }[] = [];
    tableHeaders = [{ text: this.$i18n.t('groups.name'), value: 'group.name', align: 'left', sortable: false }];

    @Ref() form!: any;

    // ✅ NOVÝ SYSTÉM: Kontroluje, zda je audit readonly podle kategorie-specifických oprávnění
    get isReadonly(): boolean {
        if (!this.audit || !this.audit.categoryId) return true;
        
        // Kontrola EditAllAudits s kontextovými oprávněními
        if (this.hasAccessToCategory(this.audit.categoryId, [PermissionTypeEnum.EditAllAudits])) {
            return false;
        }

        // Kontrola EditOwnedAudit s kontextovými oprávněními (musí být owner)
        if (this.audit.ownerId === this.tenantUser.id && 
            this.hasAccessToCategory(this.audit.categoryId, [PermissionTypeEnum.EditOwnedAudit])) {
            return false;
        }

        return true;
    }

    @Watch('selectedGroups', { deep: true })
    questionGroupsChanged() {
        this.hasChanges = !isEqual(this.selectedGroups, this.selectedGroupsOriginal);
    }

    mounted() {
        this.audit = { ...auditsStore.useGetter(CrudGetter.Detail) } as AuditModel;
        this.auditOriginal = cloneDeep(this.audit);
        this.loadItems().then();
        this.loading = false;
    }

    public getAuditEscalationGroups(): Promise<AuditEscalationGroupModel[]> {
        return new Promise<AuditEscalationGroupModel[]>((resolve, reject) => {
            auditEscalationGroupsStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload(undefined, [
                    { field: 'auditId', op: QueryOperator.Eq, comparand: this.auditId },
                    { field: 'escalationGroupType', op: QueryOperator.Eq, comparand: EscalationGroupTypeEnum.FindingEscalation }
                ])
            );
            this.subscribe(auditEscalationGroupsStore, CrudReponse.GetAll)
                .then((existingAuditEscalationGroups: AuditEscalationGroupModel[]) => {
                    resolve(existingAuditEscalationGroups);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    public getGroups(): Promise<GroupModel[]> {
        return new Promise<GroupModel[]>((resolve, reject) => {
            groupsStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload({
                    field: 'name',
                    index: 1,
                    order: 'asc',
                })
            );
            this.subscribe(groupsStore, CrudReponse.GetAll)
                .then((groups: GroupModel[]) => {
                    resolve(groups);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    public deleteAllPromise(payload: AuditEscalationGroupModel[]): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.deleteAllPromisePop(payload, resolve, reject);
        });
    }

    public saveAllPromise(payload: AuditEscalationGroupModel[]): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.saveAllPromisePop(payload, resolve, reject);
        });
    }

    private save() {
        this.saving = true;
        auditsStore.dispatch(CrudAction.Update, { item: this.audit });
        this.subscribe(auditsStore, CrudReponse.Update)
            .then((e: AuditModel) => {
                this.audit = e;
                this.getAuditEscalationGroups()
                    .then((existingAuditEscalationGroups: AuditEscalationGroupModel[]) => {
                        let selectedGroups = this.selectedGroups;
                        const auditEscalationGroupsToDelete = existingAuditEscalationGroups.filter(
                            x => selectedGroups.find(n => n.groupId == x.groupId) == null
                        );
                        const auditEscalationGroupsToInsert = selectedGroups
                            .filter(x => existingAuditEscalationGroups.find(n => n.groupId == x.groupId) == null)
                            .map(x => {
                                return {
                                    id: x.auditEscalationGroup?.id ?? Guid.EmptyGuid(),
                                    auditId: this.auditId,
                                    groupId: x.groupId,
                                    escalationGroupType: EscalationGroupTypeEnum.FindingEscalation,
                                } as AuditEscalationGroupModel;
                            });
                        this.deleteAllPromise(auditEscalationGroupsToDelete)
                            .then(() => {
                                this.saveAllPromise(auditEscalationGroupsToInsert)
                                    .then(() => {
                                        this.loadItems()
                                            .then(() => {
                                                this.saving = false;
                                            })
                                            .catch((e: any) => {
                                                this.saving = false;
                                                console.error(e);
                                            });
                                    })
                                    .catch((e: any) => {
                                        this.saving = false;
                                        console.error(e);
                                    });
                            })
                            .catch((e: any) => {
                                this.saving = false;
                                console.error(e);
                            });
                    })
                    .catch((e: any) => {
                        this.saving = false;
                        console.error(e);
                    });
            })
            .catch((e: any) => {
                this.saving = false;
                console.error(e);
            });
    }

    private loadItems(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.getAuditEscalationGroups()
                .then((existingAuditEscalationGroups: AuditEscalationGroupModel[]) => {
                    this.getGroups()
                        .then((groups: WorkplaceModel[]) => {
                            const deletedWorkplaces = existingAuditEscalationGroups!.filter(x => groups.find(n => n.id == x.groupId) == null);
                            this.auditEscalationGroups = [
                                ...deletedWorkplaces.map(x => {
                                    return {
                                        groupId: x.groupId!,
                                        auditEscalationGroup: x,
                                        group: x.group,
                                    };
                                }),
                            ];
                            this.selectedGroups = [
                                ...deletedWorkplaces.map(x => {
                                    return {
                                        groupId: x.groupId!,
                                        auditEscalationGroup: null,
                                        group: null,
                                    };
                                }),
                            ];
                            this.auditEscalationGroups = [
                                ...this.auditEscalationGroups,
                                ...groups.map(x => {
                                    return {
                                        groupId: x.id!,
                                        auditEscalationGroup: existingAuditEscalationGroups!.find(n => n.groupId == x.id) ?? {
                                            id: Guid.EmptyGuid(),
                                        },
                                        group: x,
                                    };
                                }),
                            ];
                            this.selectedGroups = [
                                ...existingAuditEscalationGroups!.map(x => {
                                    return {
                                        groupId: x.groupId!,
                                        auditEscalationGroup: null,
                                        group: null,
                                    };
                                }),
                            ];
                            this.selectedGroupsOriginal = [...this.selectedGroups];
                            resolve(undefined);
                        })
                        .catch((e: any) => reject(e));
                })
                .catch((e: any) => reject(e));
        });
    }

    private deleteAllPromisePop(payload: AuditEscalationGroupModel[], resolve: (value: any) => void, reject: (reason?: any) => void) {
        if (payload.length > 0) {
            const payloadItem = payload[0];
            if (payloadItem.id != Guid.EmptyGuid()) {
                auditEscalationGroupsStore.dispatch(CrudAction.Delete, { id: payloadItem.id });
                this.subscribe(auditEscalationGroupsStore, CrudReponse.Delete)
                    .then((e: any) => {
                        payload.shift();
                        this.deleteAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            }
        } else {
            resolve(null);
        }
    }

    private saveAllPromisePop(payload: AuditEscalationGroupModel[], resolve: (value: any) => void, reject: (reason?: any) => void) {
        if (payload.length > 0) {
            const payloadItem = payload[0];
            if (payloadItem.id == Guid.EmptyGuid()) {
                auditEscalationGroupsStore.dispatch(CrudAction.Create, { item: payloadItem });
                this.subscribe(auditEscalationGroupsStore, CrudReponse.Create)
                    .then((e: any) => {
                        payload.shift();
                        this.saveAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            } else {
                auditEscalationGroupsStore.dispatch(CrudAction.Update, { item: payloadItem });
                this.subscribe(auditEscalationGroupsStore, CrudReponse.Update)
                    .then((e: any) => {
                        payload.shift();
                        this.saveAllPromisePop(payload, resolve, reject);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            }
        } else {
            resolve(null);
        }
    }
}
